/**
 * Server-side invoice math for a request (docs/M2-CASE-API.md §4). Pure so the API and the
 * dealer form preview compute the same numbers; the API result is the only one persisted.
 *
 *   equipment_total      = Σ quantity × unit_price
 *   financed_soft_costs  = Σ soft_costs
 *   invoice_total        = equipment_total + financed_soft_costs − discount
 *   requested_principal  = invoice_total − cash_contribution        (default)
 *
 * A dealer may override `requested_principal` only with a `reconciliation_reason`. Trade-ins
 * never enter this math; `trade_in_noted` is a routing flag handled by the caller.
 */
import { add, compare, isNegative, money, multiply, subtract, sum, type CurrencyCode, type Money } from './money'

export interface PricingLineInput {
  quantity: number
  unitPrice: Money
}

export interface PricingInput {
  equipment: readonly PricingLineInput[]
  softCosts: readonly Money[]
  discount?: Money
  cashContribution?: Money
  requestedPrincipal?: Money
  reconciliationReason?: string | null
  currency?: CurrencyCode
}

export interface PricingResult {
  equipmentTotal: Money
  financedSoftCosts: Money
  discount: Money
  invoiceTotal: Money
  cashContribution: Money
  requestedPrincipal: Money
  /** Null when `requestedPrincipal` equals the default. */
  reconciliationReason: string | null
  lineTotals: Money[]
}

export type PricingErrorCode =
  | 'no_equipment'
  | 'quantity_invalid'
  | 'negative_amount'
  | 'discount_exceeds_subtotal'
  | 'cash_contribution_exceeds_invoice'
  | 'requested_principal_not_positive'
  | 'reconciliation_required'
  | 'reconciliation_without_override'
  | 'currency_mismatch'

export class PricingError extends Error {
  constructor(
    readonly code: PricingErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'PricingError'
  }
}

function nonNegative(m: Money, what: string): Money {
  if (isNegative(m)) throw new PricingError('negative_amount', `${what} must not be negative`)
  return m
}

export function computePricing(input: PricingInput): PricingResult {
  const currency = input.currency ?? 'USD'
  if (input.equipment.length === 0) {
    throw new PricingError('no_equipment', 'at least one equipment line is required')
  }
  const everyMoney: Money[] = [
    ...input.equipment.map((l) => l.unitPrice),
    ...input.softCosts,
    ...(input.discount ? [input.discount] : []),
    ...(input.cashContribution ? [input.cashContribution] : []),
    ...(input.requestedPrincipal ? [input.requestedPrincipal] : []),
  ]
  for (const m of everyMoney) {
    if (m.currency !== currency) {
      throw new PricingError('currency_mismatch', `all amounts must be ${currency}, got ${m.currency}`)
    }
  }

  const lineTotals = input.equipment.map((l) => {
    if (!Number.isSafeInteger(l.quantity) || l.quantity < 1) {
      throw new PricingError('quantity_invalid', 'quantity must be a positive integer')
    }
    return multiply(nonNegative(l.unitPrice, 'unit_price'), l.quantity)
  })
  const equipmentTotal = sum(lineTotals, currency)
  const financedSoftCosts = sum(
    input.softCosts.map((m) => nonNegative(m, 'soft_cost')),
    currency,
  )
  const discount = nonNegative(input.discount ?? money(0, currency), 'discount')
  const subtotal = add(equipmentTotal, financedSoftCosts)
  if (compare(discount, subtotal) > 0) {
    throw new PricingError('discount_exceeds_subtotal', 'discount cannot exceed equipment plus soft costs')
  }
  const invoiceTotal = subtract(subtotal, discount)

  const cashContribution = nonNegative(input.cashContribution ?? money(0, currency), 'cash_contribution')
  if (compare(cashContribution, invoiceTotal) > 0) {
    throw new PricingError('cash_contribution_exceeds_invoice', 'cash contribution cannot exceed the invoice total')
  }
  const defaultPrincipal = subtract(invoiceTotal, cashContribution)

  const reason = input.reconciliationReason?.trim() ? input.reconciliationReason.trim() : null
  let requestedPrincipal = defaultPrincipal
  let reconciliationReason: string | null = null
  if (input.requestedPrincipal && compare(input.requestedPrincipal, defaultPrincipal) !== 0) {
    if (!reason) {
      throw new PricingError(
        'reconciliation_required',
        'requested_principal differs from invoice_total − cash_contribution; reconciliation_reason is required',
      )
    }
    requestedPrincipal = nonNegative(input.requestedPrincipal, 'requested_principal')
    reconciliationReason = reason
  } else if (reason) {
    throw new PricingError(
      'reconciliation_without_override',
      'reconciliation_reason is only accepted when requested_principal differs from the default',
    )
  }
  if (requestedPrincipal.amountMinor <= 0) {
    throw new PricingError('requested_principal_not_positive', 'there is nothing to finance')
  }

  return {
    equipmentTotal,
    financedSoftCosts,
    discount,
    invoiceTotal,
    cashContribution,
    requestedPrincipal,
    reconciliationReason,
    lineTotals,
  }
}
